import React, { useState, useEffect, useContext } from "react";
import {
  TextField,
  Grid,
  Container,
  Button,
  Typography,
  Box,
} from "@mui/material";
import MarkupTextEditor from "../../components/utilities/MarkupTextEditor";
import ImageUploader, {
  uploadBas64ToS3,
} from "../../components/utilities/ImageUploader";
import { Formik } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import { postNotification } from "../../components/utilities/Notifications";
import GenericPicker from "../../components/utilities/GenericPicker";
import MUITags from "../../components/utilities/MUITags";
import DatePickerComponent from "../../components/utilities/DatePickerComponent";
import moment from "moment";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import { useConfirm } from "material-ui-confirm";
import HelpButton from "../../components/utilities/HelpButton";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { BCPFloppyDisk, BCPTrash } from "../../components/icons/bcp/BCPIcons";
import {
  deleteData,
  getData,
  postData,
} from "../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../global/environmentVariables";

export default function PostEditor() {
  const [loading, setLoading] = useState(true);
  const [post, setPost] = useState(null);
  const [authors, setAuthors] = useState(null);
  const { postId } = useParams();
  const [processing, setProcessing] = useState(false);
  const authContext = useContext(AuthContext);
  const testAuthors = [
    {
      id: authContext?.authState?.identity?.userId,
      firstName: authContext?.authState?.identity?.firstName,
      lastName: authContext?.authState?.identity?.lastName,
    },
  ];

  const screenWidth = window.innerWidth;

  const confirm = useConfirm();

  useEffect(() => {
    const go = async () => {
      try {
        let apiPost;

        if (postId) {
          let apiData = await getData(`/pages/${postId}`, {
            env: getEnvironmentVariable("COMPANY_FLAG"),
          });
          apiPost = { ...apiData };
        }

        setPost(apiPost);
        setAuthors(testAuthors);
        setLoading(false);
      } catch (err) {
        postNotification("error", err);
        window.location.assign("/");
      }
    };

    go();
    // eslint-disable-next-line
  }, [postId]);

  return (
    <Box sx={{ width: "100%" }}>
      {Boolean(loading) && <ModalLoadingFull />}
      {Boolean(processing) && <ModalLoadingFull />}

      <Container
        maxWidth="lg"
        sx={{
          pt: 2,
          pb: 2,
        }}
      >
        <Grid container sx={{ gap: 2, p: 2 }}>
          <Grid
            item
            xs={12}
            sx={{ p: 2, borderBottom: "1px solid rgba(0, 0, 0, 0.12)" }}
          >
            <Typography variant="h3" gutterBottom>
              Post Editor
            </Typography>
          </Grid>

          <Grid item xs={12}>
            {!loading && (
              <Formik
                initialValues={{
                  title: post?.title || "",
                  subTitle: post?.subTitle || "",
                  body: post?.body || "",
                  pageType: post?.pageType || "post",
                  permalink: post?.permalink || "",
                  authorId: post?.authorId || "",
                  status: post?.status || "draft",
                  tags: post?.tags || [],
                  tagsList: post?.tags?.length > 0 ? post.tags.join(",") : "",
                  featuredImageId: post?.featuredImageId || "",
                  headerImageId: post?.headerImageId || "",
                  sortDate: post?.sortDate
                    ? moment(post.sortDate).format("YYYY-MM-DD")
                    : moment().startOf("day").format("YYYY-MM-DD"),
                }}
                validationSchema={Yup.object({
                  title: Yup.string().required("Required field."),
                  body: Yup.string().required("Required field."),
                  pageType: Yup.string().required("Required field."),
                  permalink: Yup.string()
                    .required("Required field.")
                    .min(3)
                    .max(40)
                    .matches(
                      /^[a-z0-9_-]*$/,
                      'Must contain only alphanumeric, "_", and "-"'
                    ),
                  authorId: Yup.string().required("Required field."),
                  status: Yup.string().required("Required field."),
                })}
                enableReinitialize
                onSubmit={async (values) => {
                  try {
                    setProcessing(true);

                    var sanitizedValues = {
                      ...values,
                      body: values?.body
                        ? values.body.replace(
                            /(?<!\n)(?<!^)(?<!\|)\n(?!\n)(?!$)(?!\|)(?!^\s*(\*|\d+\.\s))/gm,
                            "\n\n"
                          )
                        : "",

                      sortDate: moment(values.sortDate).utc().format(),
                    };

                    // Handles images
                    if (sanitizedValues.featuredImage) {
                      // upload image to s3
                      let newFile = await uploadBas64ToS3(
                        sanitizedValues.featuredImage
                      );
                      delete sanitizedValues.featuredImage;
                      if (newFile.fileId) {
                        sanitizedValues.featuredImageId = newFile.fileId;
                      }
                    }
                    if (sanitizedValues.headerImage) {
                      // upload image to s3
                      let newFile = await uploadBas64ToS3(
                        sanitizedValues.headerImage
                      );
                      delete sanitizedValues.headerImage;
                      if (newFile.fileId) {
                        sanitizedValues.headerImageId = newFile.fileId;
                      }
                    }

                    // Handles updates to existing posts
                    if (post?.id) {
                      setPost(sanitizedValues);

                      let updatedPost = await postData(`/pages/${post.id}`, {
                        env: getEnvironmentVariable("COMPANY_FLAG"),
                        set: sanitizedValues,
                      });

                      // Simply notifies user page/post type was updated
                      postNotification(
                        "success",
                        `${
                          sanitizedValues.pageType.charAt(0).toUpperCase() +
                          sanitizedValues.pageType.slice(1)
                        } updated.`
                      );

                      window.location.assign(`/cms/post/${updatedPost.id}`);

                      // Handles the creation of new posts
                    } else {
                      if (sanitizedValues.pageType === "page") {
                        delete sanitizedValues.tags;
                      }

                      setPost(sanitizedValues);

                      let newPost = await postData("/pages", {
                        body: {
                          env: getEnvironmentVariable("COMPANY_FLAG"),
                          ...sanitizedValues,
                        },
                      });

                      // Simply notifies user page/post type was created
                      postNotification(
                        "success",
                        `${
                          sanitizedValues.pageType.charAt(0).toUpperCase() +
                          sanitizedValues.pageType.slice(1)
                        } created.`
                      );

                      setTimeout(() => {
                        window.location.assign(`/cms/post/${newPost.id}`);
                      }, 1500);
                    }
                  } catch (err) {
                    postNotification("error", err);
                    console.log(err);
                  }
                  setProcessing(false);
                }}
              >
                {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  touched,
                  values,
                  setFieldValue,
                }) => (
                  // Page is layed out in two major columns. One big, wide one on the left for the editor, and one small one on the right for the textfields
                  <>
                    <form onSubmit={handleSubmit}>
                      <Grid
                        container
                        spacing={{
                          xs: 2,
                          md: 4,
                        }}
                        direction={{
                          xs: "column-reverse",
                          md: "row",
                        }}
                      >
                        <Grid item xs={12} md={8}>
                          <MarkupTextEditor
                            name={values.title}
                            onChange={({ text, html }) => {
                              setFieldValue("body", text);
                            }}
                            onBlur={handleBlur}
                            value={values.body}
                            disabled={false}
                            sx={{
                              height: screenWidth > 900 ? "1000px" : "600px",
                            }}
                            error={Boolean(touched.body && errors.body)}
                            helperText={touched.body && errors.body}
                          />
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <Grid container gap={1}>
                            <Grid item xs={12}>
                              <TextField
                                variant="outlined"
                                label="Title"
                                name="title"
                                id="title"
                                value={values.title}
                                onChange={(e) => {
                                  // setFieldValue("title", e.target.value);
                                  handleChange(e);
                                  if (!touched.permalink && !post?.id) {
                                    let permalink = e.target.value
                                      .split(" ")
                                      .join("-")
                                      .toLowerCase()
                                      .replace(/[^a-z0-9_-]+/g, "");

                                    setFieldValue(
                                      "permalink",
                                      permalink,
                                      false
                                    );
                                  }
                                }}
                                onBlur={handleBlur}
                                fullWidth
                                required
                                error={Boolean(touched.title && errors.title)}
                                helperText={touched.title && errors.title}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <TextField
                                variant="outlined"
                                label="Subtitle"
                                name="subTitle"
                                id="subTitle"
                                value={values.subTitle}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                                error={Boolean(
                                  touched.subTitle && errors.subTitle
                                )}
                                helperText={touched.subTitle && errors.subTitle}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <GenericPicker
                                name="pageType"
                                label="Type"
                                value={values.pageType}
                                handleChange={handleChange}
                                list={[
                                  { label: "Post", value: "post" },
                                  { label: "Page", value: "page" },
                                ]}
                                required
                                inclAny={false}
                                error={Boolean(
                                  touched.pageType && errors.pageType
                                )}
                                helperText={touched.pageType && errors.pageType}
                                disabled={post?.id}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <DatePickerComponent
                                name="sortDate"
                                label="Date"
                                value={values.sortDate}
                                setFieldValue={setFieldValue}
                                error={Boolean(
                                  touched.sortDate && errors.sortDate
                                )}
                                helperText={touched.sortDate && errors.sortDate}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <TextField
                                variant="outlined"
                                label="Permalink"
                                name="permalink"
                                id="permalink"
                                value={values.permalink}
                                onChange={(e) => {
                                  let permalink = e.target.value
                                    .split(" ")
                                    .join("-")
                                    .toLowerCase()
                                    .replace(/[^a-z0-9_-]+/g, "");

                                  setFieldValue("permalink", permalink, false);
                                }}
                                onBlur={handleBlur}
                                fullWidth
                                required
                                error={Boolean(
                                  touched.permalink && errors.permalink
                                )}
                                helperText={
                                  touched.permalink && errors.permalink
                                }
                              />
                            </Grid>

                            {values.pageType !== "page" && (
                              <Grid item xs={12}>
                                <MUITags
                                  label="Tags"
                                  name="tagsList"
                                  value={values.tagsList}
                                  valueArray={values.tags}
                                  nameArray={"tags"}
                                  setFieldValue={setFieldValue}
                                  handleBlur={handleBlur}
                                  handleChange={handleChange}
                                  error={Boolean(
                                    touched.tagsList && errors.tagsList
                                  )}
                                  helperText={
                                    touched.tagsList && errors.tagsList
                                  }
                                  sx={{ margin: 0 }}
                                />
                              </Grid>
                            )}

                            <Grid item xs={12}>
                              <GenericPicker
                                name="authorId"
                                label="Author"
                                value={values.authorId}
                                handleChange={handleChange}
                                list={authors.map((el) => {
                                  return {
                                    value: el.id,
                                    label: `${el.firstName || ""} ${
                                      el.lastName || ""
                                    }`.trim(),
                                  };
                                })}
                                required
                                inclAny={false}
                                error={Boolean(
                                  touched.authorId && errors.authorId
                                )}
                                helperText={touched.authorId && errors.authorId}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <Typography variant="body1">
                                Header Image
                                <HelpButton
                                  title={`The Header Image will be displayed in the header section of this ${values.pageType}. The ${values.pageType} title written over the top of the image. It will resize to fit the width of the user’s screen (the edges of the image may be cropped).`}
                                />
                              </Typography>
                              <ImageUploader
                                values={values}
                                label="Header Image"
                                name="headerImage"
                                fileId={values.headerImageId}
                                setFieldValue={setFieldValue}
                                aspectW={15}
                                aspectY={4}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <Typography variant="body1">
                                Featured Image
                                <HelpButton
                                  title={`The Featured Image will be displayed on the thumbnail for this ${values.pageType}`}
                                />
                              </Typography>
                              <ImageUploader
                                values={values}
                                label="Featured Image"
                                name="featuredImage"
                                fileId={values.featuredImageId}
                                setFieldValue={setFieldValue}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <GenericPicker
                                name="status"
                                label="Status"
                                value={values.status}
                                handleChange={handleChange}
                                list={[
                                  { label: "Draft", value: "draft" },
                                  { label: "Publish", value: "published" },
                                ]}
                                inclAny={false}
                                error={Boolean(touched.status && errors.status)}
                                helperText={touched.status && errors.status}
                              />
                            </Grid>

                            {post?.id && post.status === "published" && (
                              <Grid item xs={12}>
                                <Button
                                  variant="outlined"
                                  fullWidth
                                  type="button"
                                  target="_blank"
                                  href={`/${post.permalink}`}
                                >
                                  {`View ${post.pageType}`}
                                </Button>
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Separate Grid for submit button so that it always remains at the bottom regardless of the above container's direction order */}
                      <Grid
                        container
                        spacing={{
                          xs: 2,
                          md: 4,
                        }}
                        sx={{ pt: 2, display: "flex", alignItems: "center" }}
                      >
                        {postId && (
                          <Grid item xs={12} md={4}>
                            <Button
                              fullWidth
                              variant="contained"
                              startIcon={<BCPTrash />}
                              color="error"
                              onClick={async () => {
                                try {
                                  await confirm({
                                    title: `Are you sure?`,
                                    description: `Are you sure you want to delete this ${values.pageType}?`,
                                    dialogProps: {
                                      sx: {
                                        zIndex: 99999,
                                      },
                                    },
                                  });
                                } catch (err) {
                                  return;
                                }

                                try {
                                  await confirm({
                                    title: `Are you really sure?`,
                                    description: `Are you really sure you want to delete this ${values.pageType}? This can't be undone.`,
                                    dialogProps: {
                                      sx: {
                                        zIndex: 99999,
                                      },
                                    },
                                  });
                                } catch (err) {
                                  return;
                                }

                                try {
                                  await deleteData(`/pages/${post.id}`);
                                  window.location.assign("/cms");
                                } catch (err) {
                                  console.log(err);
                                  postNotification("error", err);
                                }
                              }}
                            >
                              Delete
                            </Button>
                          </Grid>
                        )}
                        <Grid item xs={12} md={8}>
                          <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            startIcon={<BCPFloppyDisk />}
                            disabled={loading || processing ? true : false}
                          >
                            Save
                          </Button>
                        </Grid>
                      </Grid>
                    </form>
                  </>
                )}
              </Formik>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
